import React, { useEffect, useRef, useState } from "react";
import ProductListCommon from "./ProductListCommon";
import { Badge, Fab } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from 'yup';
import { useAppContext } from "../../share-components/AppContext";
import { getAllShops } from "../../services/ShopService";
import FormTitle from "../../share-components/FormTitle";
import { Button, Card } from "react-bootstrap";
import { PAGE_PURCHASE_EDIT, ROUTE_PURCHASE_DETAILS, ROUTE_PURCHASE_LIST } from "../../constants/routeConstants";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { isNullOrEmptyString, scrollToFormError, scrollToTop } from "../../services/UtilityService";
import ConfirmDialog from "../../share-components/ConfirmDialog";
import AlertDialog from "../../share-components/AlertDialog";
import { editPurchase, getEditPurchaseInfo } from "../../services/ManagementService";
import noImage from '../../assets/No_Image_Available.jpg';
import { SCROLL_RESTORE_PREFIX_KEY, SERVER_ERROR_MESSAGE } from "../../constants/commonConstants";

const PurchaseEdit = () => {
    const { purchaseId } = useParams();
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [productListShow, setProductListShow] = useState(false);
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [createdPurchaseId, setCreatedPurchaseId] = useState(null);
    const [shopList, setShopList] = useState([]);
    const navigate = useNavigate();
    const [purchaseBaseInfo, setPurchaseBaseInfo] = useState(null);
    const [isFirstLoadedFromChild, setIsFirstLoadedFromChild] = useState(false);
    const [originalCart, setOriginalCart] = useState([]);
    const formikRef = useRef();


    useEffect(() => {
        setRouteNaviData("PRODUCT >> EDIT PURCHASE INFO");
        sessionStorage.removeItem(`${SCROLL_RESTORE_PREFIX_KEY}${PAGE_PURCHASE_EDIT}`);
        fetchInitialLoad();
    }, []);

    const fetchInitialLoad = async () => {
        setLoading(true);
        try {
            const response = await getAllShops();
            const allShops = response?.allShops || [];
            if (allShops) {
                const shopListOptions = allShops.map((shop) => (
                    shop.shopName
                ));
                setShopList(shopListOptions || []);
            }
            const pInfo = await getEditPurchaseInfo(purchaseId);
            const oldPurchaseCart = (pInfo?.purchasedProductData || []).map(productData => {
                let productDataForPurchase = {
                    productId: productData?.productId,
                    productName: productData?.productName,
                    brandName: productData?.brandName,
                    categoryName: productData?.categoryName,
                    productPicture: productData?.productPicture,
                    itemInfo: (productData?.itemInfo || []).map(item => ({
                        color: item.color,
                        colorId: item.colorId,
                        size: item.size,
                        sizeId: item.sizeId,
                        inventoryItemId: item.inventoryItemId,
                        quantity: item.purchasedQuantity,
                        purchasedAmount: item.purchasedAmount
                    })),
                    selectedColorId: null,
                    selectedSizeId: null,
                    colors: productData?.colors || [],
                    sizes: productData?.sizes || [],
                    isValidForm: true,
                    quantity: null,
                    purchasedAmount: null
                }
                return productDataForPurchase;
            })
            // setSelectedProduct(oldPurchaseCart || []);
            setOriginalCart(oldPurchaseCart || []);

            const baseInfo = {
                discountAmount: pInfo?.purchaseInfo?.discountAmount,
                receivedFlag: pInfo?.purchaseInfo?.receivedFlag,
                shopName: pInfo?.purchaseInfo?.shopName,
                comment: pInfo?.purchaseInfo?.comment
            }
            setPurchaseBaseInfo(baseInfo);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isFirstLoadedFromChild) {
            setLoading(false);
        }
    }, [isFirstLoadedFromChild]);

    const toggleProductlistShow = () => {
        setProductListShow(prev => !prev);
    }

    useEffect(() => {
        if (!productListShow) {
            scrollToTop(true);
        }
    }, [productListShow])

    const initialValues = {
        discountAmount: purchaseBaseInfo?.discountAmount || "",
        receivedFlag: purchaseBaseInfo?.receivedFlag || false,
        shopName: purchaseBaseInfo?.shopName || '',
        comment: purchaseBaseInfo?.comment || '',
    };

    const validationSchema = Yup.object({
        discountAmount: Yup.number().nullable()
            .transform((value) => (Number.isNaN(value) || parseInt(value) < 0 ? null : value))
            .max(1000000, 'Discount Amount must not exceed 1000000¥.')
            .test(
                'max-discount',
                'Discount amount cannot exceed total purchased amount.',
                function (value) {
                    const { selectedProduct } = this.options.context;
                    if (value == null) return true;

                    return value <= totalPurchasedAmount;
                }
            ),
        receivedFlag: Yup.boolean(),
        shopName: Yup.string().nullable()
            .max(30, 'Shop Name must not exceed 30 characters.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.'),
    });

    const handleSetFormValidation = () => {
        setSelectedProduct(prev => {
            return prev.map(product => {
                const isValid = (product.itemInfo || []).some(item => !isNullOrEmptyString(item.quantity) && !isNullOrEmptyString(item.purchasedAmount));
                if (isValid) {
                    return {
                        ...product,
                        isValidForm: true
                    }
                } else {
                    return {
                        ...product,
                        isValidForm: false
                    }
                }
            })
        });
    }

    const onSubmit = async (form) => {
        if ((selectedProduct || []).length <= 0) return;
        // handleSetFormValidation();
        const isValid = (selectedProduct || []).every(product => (product.itemInfo || []).some(item => !isNullOrEmptyString(item.quantity) && !isNullOrEmptyString(item.purchasedAmount)));
        if (isValid) {
            setFormValue(form);
            setShowConfirmDialog(true);
        } else {
            handleSetFormValidation();
        }
    };

    const handlePurchaseProduct = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const formData = {
                purchaseId: purchaseId,
                purchaseInfo: {
                    discountAmount: formValue.discountAmount || 0,
                    receivedFlag: formValue.receivedFlag,
                    shopName: formValue.shopName,
                    comment: formValue.comment
                },
                purchaseItemList: ((selectedProduct || []).filter(product => product.itemInfo.some(item => !isNullOrEmptyString(item.quantity) && item.quantity > 0 &&
                    !isNullOrEmptyString(item.purchasedAmount) && item.purchasedAmount > 0))
                    .flatMap(product => product.itemInfo.filter(item => !isNullOrEmptyString(item.quantity) && item.quantity > 0 &&
                        !isNullOrEmptyString(item.purchasedAmount) && item.purchasedAmount > 0)).map(item => ({
                            inventoryItemId: item.inventoryItemId,
                            purchasedQuantity: item.quantity,
                            purchasedAmount: item.purchasedAmount
                        }))),
            }
            const createdId = await editPurchase(formData);
            setCreatedPurchaseId(createdId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = () => {
        if ((parseInt(createdPurchaseId) || 0) > 0) {
            navigate(`${ROUTE_PURCHASE_DETAILS}/${createdPurchaseId}`);
        } else {
            navigate(ROUTE_PURCHASE_LIST);
        }
    }

    const updateChangedProductField = (productId, field, value) => {
        setSelectedProduct(prev =>
            (prev || []).map(product =>
                product.productId === productId
                    ? {
                        ...product,
                        [field]: !isNullOrEmptyString(value) ? value : null,
                    }
                    : product
            )
        );
    };


    const handleColorChange = (productId, colorId) => {
        const value = parseInt(colorId) || null;
        updateChangedProductField(productId, 'selectedColorId', value);
    };

    const handleSizeChange = (productId, sizeId) => {
        const value = parseInt(sizeId) || null;
        updateChangedProductField(productId, 'selectedSizeId', value);
    };

    const handleQuantityChange = (productId, quantity) => {
        if (isNullOrEmptyString(productId)) return;

        const convertedQty = parseInt(quantity);
        const finalQty = convertedQty > 0 ? convertedQty : null;

        if (finalQty > 10000) return;

        updateChangedProductField(productId, 'quantity', finalQty);
    };

    const handlePurchasedAmountChange = (productId, amount) => {
        if (isNullOrEmptyString(productId)) return;

        const convertedAmount = parseInt(amount);
        const finalAmount = convertedAmount > 0 ? convertedAmount : null;

        if (finalAmount > 10000000) return;

        updateChangedProductField(productId, 'purchasedAmount', finalAmount);
    };

    const isEnableAddButton = (productId) => {
        if (isNullOrEmptyString(productId)) return false;
        const productIdNumber = parseInt(productId) || 0;
        const targetProduct = selectedProduct?.find(product => product.productId === productIdNumber);
        if (!targetProduct) return false;

        const quantity = targetProduct?.quantity || null;
        const amount = targetProduct?.purchasedAmount || null;

        if (isNullOrEmptyString(quantity) || isNullOrEmptyString(amount)) return false;

        const isColorExist = targetProduct?.colors?.length > 0;
        const isSizeExist = targetProduct?.sizes?.length > 0;
        if (isColorExist && isSizeExist) {
            return !isNullOrEmptyString(targetProduct.selectedColorId) && !isNullOrEmptyString(targetProduct.selectedSizeId);
        } else if (isColorExist && !isSizeExist) {
            return !isNullOrEmptyString(targetProduct.selectedColorId);
        } else if (isSizeExist && !isColorExist) {
            return !isNullOrEmptyString(targetProduct.selectedSizeId);
        } else {
            return true;
        }
    }

    const handleAddToPurchaseClick = (productId) => {
        if (isNullOrEmptyString(productId)) return false;
        const productIdNumber = parseInt(productId) || 0;
        const targetProduct = selectedProduct?.find(product => product.productId === productIdNumber);
        if (!targetProduct) return false;

        setSelectedProduct(prevProducts =>
            (prevProducts || []).map(product => {
                const isTargetProduct = product.productId === productId;

                if (!isTargetProduct) return product;

                const qty = product.quantity;
                const amount = product.purchasedAmount;

                return {
                    ...product,
                    quantity: null,
                    purchasedAmount: null,
                    isValidForm: true,
                    itemInfo: product.itemInfo.map(item => {
                        const isTargetItem =
                            (isNullOrEmptyString(item.colorId) || item.colorId === product.selectedColorId) &&
                            (isNullOrEmptyString(item.sizeId) || item.sizeId === product.selectedSizeId);

                        return isTargetItem
                            ? { ...item, quantity: qty, purchasedAmount: amount }
                            : item;
                    })
                };
            })
        );
    }

    const getColorSizePairs = (itemInfo) => {
        const result = (itemInfo || []).filter(item => !isNullOrEmptyString(item.quantity) && (parseInt(item.quantity) || 0) > 0 &&
            !isNullOrEmptyString(item.purchasedAmount) && (parseInt(item.purchasedAmount) || 0) > 0);
        return result;
    }

    const getColorSizeQuantityBlock = (item) => {
        const formattedString = [
            !isNullOrEmptyString(item.colorId) ? `[${item.color}]` : '',
            !isNullOrEmptyString(item.sizeId) ? `[${item.size}]` : '',
            !isNullOrEmptyString(item.quantity) ? `x(${item.quantity})` : '',
            !isNullOrEmptyString(item.purchasedAmount) ? `[${item.purchasedAmount}¥]` : '',
        ].filter(Boolean).join(' ');
        return formattedString;
    }

    const handleRemoveProductItem = (productId, inventoryItemId) => {

        if (isNullOrEmptyString(productId)) return false;
        const productIdNumber = parseInt(productId) || 0;
        const targetProduct = selectedProduct?.find(product => product.productId === productIdNumber);
        if (!targetProduct) return false;

        setSelectedProduct(prevProducts =>
            (prevProducts || []).map(product => {
                const isTargetProduct = product.productId === productId;
                if (!isTargetProduct) return product;

                return {
                    ...product,
                    itemInfo: product.itemInfo.map(item => {
                        const isTargetItem = parseInt(item.inventoryItemId) === parseInt(inventoryItemId);

                        return isTargetItem
                            ? { ...item, quantity: null, purchasedAmount: null }
                            : item;
                    })
                };
            })
        );
    }

    const handleRemoveProductClick = (productId) => {
        setSelectedProduct(prevProducts => {
            const updatedData = prevProducts.filter((product) => product.productId !== productId);
            return updatedData;
        });
    }


    const getShowPurchaseCartData = (productData) => {
        const isDisabledProduct = productData?.isDeactivated === true;
        return (
            <div className={`${isDisabledProduct ? "disabled-div" : ""}`}>
                <div style={{
                    fontWeight: '600',
                    display: 'flex',
                }}>
                    <div style={{ flex: "1", paddingRight: "10px" }}>
                        <div><i className='bi bi-award'></i> {productData?.brandName}</div><hr className='m-0' />
                        <div><i className='bi bi-box'></i> {productData?.productName}</div><hr className='m-0' />
                        <div><i className='bi bi-tags'></i> {productData?.categoryName}</div><hr className='m-0' />

                    </div>
                    <div style={{ paddingTop: "5px" }}>
                        <img
                            src={productData?.productPicture ? `data:image/jpeg;base64,${productData?.productPicture}` : noImage}
                            alt="picture"
                            className="img-thumbnail"
                            style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                        />
                    </div>
                </div>
                {productData?.isValidForm === false && <div className="formErrDiv text-danger">Add your purchase item.</div>}

                {getColorSizePairs(productData?.itemInfo).length > 0 && <div className="mb-1 colorSizeBlockSection">
                    <div className='colorSizeBlockWrapper mb-2'>
                        {getColorSizePairs(productData?.itemInfo).map((item) => (
                            <span key={item.inventoryItemId} className='colorSizeBlock'>{getColorSizeQuantityBlock(item)}
                                <button type='button' className='icon-btn p-0 ms-2 text-danger'
                                    onClick={() => { handleRemoveProductItem(productData?.productId, item.inventoryItemId); }}>
                                    <i className="bi bi-x-circle-fill"></i></button>
                            </span>
                        ))}
                    </div>
                </div>}

                {!isDisabledProduct ? <>
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'end',
                        flexWrap: 'wrap'
                    }}>
                        {productData?.colors?.length > 0 &&

                            <div className="d-flex flex-column">
                                <label className="custom-label">Color</label>
                                <select className="customInputControl" value={isNullOrEmptyString(productData.selectedColorId) ? "" : productData.selectedColorId}
                                    style={{
                                        minWidth: '80px',
                                        maxWidth: '120px'
                                    }}
                                    onChange={(e) => handleColorChange(productData?.productId, e.target.value)}>
                                    <option value="" disabled>--Select--</option>
                                    {productData?.colors?.map((data, index) => (
                                        <option key={index} value={data?.colorId}>{data?.color}</option>
                                    ))}
                                </select>
                            </div>}

                        {productData?.sizes?.length > 0 &&
                            <div className="d-flex flex-column">
                                <label className="custom-label">Size</label>
                                <select className="customInputControl" value={isNullOrEmptyString(productData.selectedSizeId) ? "" : productData.selectedSizeId}
                                    style={{
                                        minWidth: '80px',
                                        maxWidth: '120px'
                                    }}
                                    onChange={(e) => handleSizeChange(productData?.productId, e.target.value)}>
                                    <option value="" disabled>--Select--</option>
                                    {productData?.sizes?.map((data, index) => (
                                        <option key={index} value={data?.sizeId}>{data?.size}</option>
                                    ))}
                                </select>
                            </div>}
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'end',
                        flexWrap: 'wrap'
                    }}>
                        <div className="d-flex flex-column">
                            <label className="custom-label">Quantity</label>
                            <input value={productData?.quantity?.toString() || ""}
                                type="number"
                                inputMode="numeric"  // forces numeric keypad on mobile
                                pattern="[0-9]*"      // enforces only digits
                                className="w-100 customInputControl clearNumberSpin"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                        e.preventDefault();
                                    }

                                }}
                                onChange={(e) => handleQuantityChange(productData?.productId, e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                style={{
                                    minWidth: '80px',
                                    maxWidth: '120px'
                                }}
                            />
                        </div>

                        <div className="d-flex flex-column">
                            <label className="custom-label">Purchased Price</label>
                            <input value={productData?.purchasedAmount?.toString() || ""}
                                type="number"
                                inputMode="numeric"  // forces numeric keypad on mobile
                                pattern="[0-9]*"      // enforces only digits
                                className="w-100 customInputControl clearNumberSpin"

                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                onWheel={(e) => e.target.blur()}
                                onChange={(e) => handlePurchasedAmountChange(productData?.productId, e.target.value)}
                                style={{
                                    minWidth: '80px',
                                    maxWidth: '120px'
                                }}
                            />
                        </div>
                    </div>

                    <div className="mt-2" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <button type='button' className="btn btn-warning btn-sm appBtn"
                            onClick={() => handleAddToPurchaseClick(productData?.productId)}
                            disabled={!isEnableAddButton(productData.productId)}
                        // onClick={() => {setTargetOrderId(productData?.productId); setTriggerOrder(prev => !prev)}}
                        ><i className="bi bi-cart-plus"></i>Add to Purchase</button>

                        <button type='button' className="btn btn-danger btn-sm appBtn"
                            onClick={() => handleRemoveProductClick(productData?.productId)}
                        // onClick={() => {setTargetOrderId(productData?.productId); setTriggerOrder(prev => !prev)}}
                        ><i className="bi bi-cart-x"></i>Remove</button>
                    </div>
                </> : <div className="fw-bold text-danger">
                        This Product is Deactivated!
                    </div>}
            </div>
        )
    };

    const totalPurchasedAmount = (selectedProduct || []).reduce(
        (total, product) =>
            total +
            (product.itemInfo || []).reduce(
                (sum, item) => sum + (parseInt(item.purchasedAmount) || 0),
                0
            ),
        0
    );

    const totalItemQuantity = (selectedProduct || []).reduce((total, product) => {
        const itemSum = product.itemInfo.reduce((sum, item) => sum + item.quantity, 0);
        return total + itemSum;
    }, 0);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.validateForm();
        }
    }, [selectedProduct]);

    return (
        <div style={{
            display: `${isFirstLoadedFromChild ? 'block' : 'none'}`
        }}>
            <FormTitle title={`Edit Purchase Info`} isCenter={true} />
            {isFirstLoaded && <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}>
                {({ values, setFieldTouched, setFieldValue, errors, isValid, dirty }) => {
                    const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                    return (
                        <div>
                            <Form>
                                <div style={{
                                    display: `${productListShow ? 'block' : 'none'}`
                                }}>
                                    <ProductListCommon isForPurchase={true} setProductListShow={setProductListShow}
                                        selectedParentProduct={selectedProduct} parentProductListShow={productListShow} toggleProductListShow={toggleProductlistShow}
                                        setParentSelectedProduct={setSelectedProduct} setFirstLoadedTrigger={setIsFirstLoadedFromChild}
                                        parentOriginalCart={originalCart} pageName={PAGE_PURCHASE_EDIT} />
                                </div>
                                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px', display: `${!productListShow ? 'block' : 'none'}` }}>
                                    <Card.Body className='pt-2'>
                                        <div>
                                            <>
                                                <div id='createProductPage' style={{ paddingBottom: '50px' }}>
                                                    <div className="container">
                                                        {/* <FormTitle title={"Edit Purchase Info"} isCenter={true} /> */}
                                                        <div className="row justify-content-center">
                                                            <div style={{ maxWidth: "550px" }}>
                                                                <div className="mb-2 center-block-column">
                                                                    {selectedProduct.map((product, index) => (
                                                                        <div key={index} className='info-card '
                                                                        // style={{ animationDelay: `${index * 0.6}s` }}
                                                                        >
                                                                            {getShowPurchaseCartData(product)}
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Total Item Quantity</label>
                                                                    <div className="w-100 label-box">
                                                                        {totalItemQuantity}
                                                                    </div>
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Subtotal Price</label>
                                                                    <div className="w-100 label-box">
                                                                        {totalPurchasedAmount}¥
                                                                    </div>
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Discount Price <span className="text-danger fw-bold">( - )</span></label>
                                                                    <Field name="discountAmount" type="number"
                                                                        inputMode="numeric"  // forces numeric keypad on mobile
                                                                        pattern="[0-9]*"      // enforces only digits
                                                                        className="w-100 customInputControl clearNumberSpin"
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                        onWheel={(e) => e.target.blur()}
                                                                    />
                                                                    <ErrorMessage name="discountAmount" component="div" className="formErrDiv text-danger" />
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Actual Purchased Price</label>
                                                                    <div className="w-100 label-box main-description">
                                                                        {totalPurchasedAmount - values.discountAmount}¥
                                                                    </div>
                                                                </div>

                                                                <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                                    <label className="form-check-label custom-label">
                                                                        Received Product
                                                                        <Field type="checkbox" name="receivedFlag" className="form-check-input ms-2" />
                                                                    </label>
                                                                    <ErrorMessage name="receivedFlag" component="div" className="formErrDiv text-danger" />
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Shop Name</label>
                                                                    <Field name="shopName" as="select" className="w-100 customInputControl" >
                                                                        <option value={""}>--Unknown--</option>
                                                                        {(shopList || []).map((shopName, index) => (
                                                                            <option key={index} value={shopName}>{shopName}</option>
                                                                        ))}
                                                                    </Field>
                                                                    <ErrorMessage name="shopName" component="div" className="formErrDiv text-danger" />
                                                                </div>

                                                                <div className="mb-1">
                                                                    <label className="custom-label">Comment</label>
                                                                    <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                                    <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>


                                                </div>
                                                <ConfirmDialog
                                                    dialogId='purchaseProductConfirmDialog'
                                                    showDialog={showConfirmDialog}
                                                    // title='Logout Dialog'
                                                    body='Are you sure to save purchase data for this product?'
                                                    onConfirm={handlePurchaseProduct}
                                                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                                                />
                                                <AlertDialog
                                                    dialogId={'successDialog'}
                                                    showDialog={showSuccessDialog}
                                                    body={'Product purchase data has been successfully saved.'}
                                                    onConfirm={handleSuccessDialogClose}
                                                />
                                            </>


                                            <div className='floating-bottom-btn-group'>
                                                <div className='floating-bottom-btn-grid'>
                                                    <Button
                                                        className="appBtn w-100"
                                                        type="submit"
                                                        variant="primary"
                                                        // disabled={!(isValid && dirty)}
                                                        onClick={scrollToFormError}
                                                    >
                                                        Save
                                                    </Button>
                                                    {/* <Button
                                                    className="appBtn w-100 mt-2"
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => navigate(ROUTE_PURCHASE_LIST)}
                                                >
                                                    Go to Purchase List
                                                </Button>
                                                <Button
                                                    className="appBtn w-100 mt-2"
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => navigate(`${ROUTE_PURCHASE_DETAILS}/${purchaseId}`)}
                                                >
                                                    Purchase Details
                                                </Button> */}
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Form>
                            {!productListShow && <Fab
                                color="warning"
                                sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 1 }}
                                onClick={() => toggleProductlistShow()}
                            >
                                <Badge badgeContent={"+"} color="primary">
                                    <ShoppingCartIcon />
                                </Badge>
                            </Fab>}
                        </div>
                    );
                }}
            </Formik>}

        </div>
    )
}

export default PurchaseEdit;